import React, { useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  TextField,
  Box,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { getUnixTime, parse } from 'date-fns'
import '../../../styles/pages.css'
import { TokenDurationField } from './TokenDurationField'
import { useNavigateTo } from 'infrastructure/routing'
import { useSchoolResource } from '../../../api/school.hooks'
import { useDemoResource } from '../../../api/demo.hooks'
import DeleteResourceButton from '../shared/DeleteResourceButton'
import RichTextEditor from './RichTextEditor'
import DOMPurify from 'dompurify'
import { IDemoUpdate } from 'infrastructure/api'
import { convertExpirationDate } from 'components/utility/utils'

// this allows for <a> tags to open in a new tab safely
DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  if (node.nodeName.toLowerCase() === 'a' && 'target' in node) {
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener nofollow')
  }
})

export function DemoEnvironmentEdit({ id }: { id: string }) {
  const { data: demoEnvironment, update } = useDemoResource(id)
  const schoolResource = useSchoolResource(demoEnvironment?.school_id)
  const [error, setError] = useState('')
  const navigate = useNavigateTo()

  const [tokenDuration, setTokenDuration] = useState(
    demoEnvironment?.token_duration_days ?? 1,
  )
  const [schoolName, setSchoolName] = useState(demoEnvironment?.school_name)
  const [message, setMessage] = useState(
    DOMPurify.sanitize(demoEnvironment?.message ?? ''),
  )

  const demoExpDate = convertExpirationDate(
    demoEnvironment?.expiration_date ?? '',
  )
  const [expirationDate, setExpirationDate] = useState(demoExpDate)

  if (!demoEnvironment || !schoolResource.data) {
    return <div>Loading...</div>
  }

  const onSubmit = async (e: React.FormEvent<EventTarget>) => {
    e.preventDefault()
    const req: IDemoUpdate = {
      demo_id: demoEnvironment.id,
      uuid: demoEnvironment.uuid,
      message_id: demoEnvironment.message_id,
      school_id: Number(demoEnvironment.school_id),
    }

    const sanitizedMessage = DOMPurify.sanitize(message)
    if (sanitizedMessage !== demoEnvironment.message) {
      req.message = sanitizedMessage
    }
    if (schoolName !== demoEnvironment.school_name) {
      req.school_name = schoolName
    }

    if (tokenDuration !== demoEnvironment.token_duration_days) {
      req.token_duration_days = tokenDuration
    }
    if (demoExpDate !== expirationDate) {
      const expDate = parse(expirationDate, 'yyyy-MM-dd', new Date())
      const unixTime = getUnixTime(expDate)
      req.expiration_date = unixTime
    }

    const res = await update(req)

    if (res && Array.isArray(res.errors) && res.errors.length > 0) {
      setError('Error updating Demo. Please try again.')
    } else {
      navigate(`/demos/${demoEnvironment.id}/show`)
    }
  }

  const setTokenDurationOnChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setTokenDuration(Number(event.target.value))
  }

  const setSchoolNameOnChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSchoolName(event.target.value)
  }

  const expirationDateOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpirationDate(e.target.value)
  }

  return (
    <Box m={2} sx={{ width: '70ch' }}>
      <p>
        Demo Environment: <b> {demoEnvironment.id} </b>
      </p>
      <p>
        Demo School ID: <b> {demoEnvironment.school_id} </b>
      </p>
      <TextField
        variant="outlined"
        label="Demo School Name"
        value={schoolName}
        onChange={setSchoolNameOnChange}
        fullWidth
      />
      <Box my={2} sx={{ display: 'flex', gap: '50px' }}>
        <TokenDurationField
          onChange={setTokenDurationOnChange}
          value={tokenDuration}
        />
        <TextField
          id="expiration-date"
          label="Expiration date"
          type="date"
          defaultValue={expirationDate}
          onChange={expirationDateOnChange}
          fullWidth
        />
      </Box>
      <RichTextEditor value={message} setValue={setMessage} />
      {error && <p style={{ color: 'red', textAlign: 'center' }}> {error} </p>}
      <Box my={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          color="secondary"
          onClick={onSubmit}
        >
          Update Demo
        </Button>{' '}
      </Box>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Delete School
        </AccordionSummary>
        <AccordionDetails>
          <DeleteResourceButton
            name={demoEnvironment.school_name}
            resource="Demo"
            demoID={demoEnvironment.id}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
